import { Eye, PersonStanding, Moon, Infinity as InfinityIcon, Users, Aperture, GitBranch, Wrench, HelpCircle, Activity, ArrowLeftRight, Leaf } from "lucide-react";
import { EmotionWheel } from "./widgets/EmotionWheel";
import { BodyMap } from "./widgets/BodyMap";
import { EMDRAnimator } from "./widgets/EMDRAnimator";
import { TREGuide } from "./widgets/TREGuide";

export const REALIZE = "#5E7A73";
export const DESIGNATE = "#7A6A5E";
export const RELEASE = "#8A6B7A";

// Hub metadata — 3 pillars × 4 practices. Mirrors mobile practices/index.tsx.
export const PILLARS = [
  {
    pillar: "realize", titleKey: "pillar_realize", descKey: "pillar_realize_desc", color: REALIZE,
    practices: [
      { key: "inner_critic", titleKey: "p_inner_critic", descKey: "p_inner_critic_desc", icon: Eye },
      { key: "body_scan", titleKey: "p_body_scan", descKey: "p_body_scan_desc", icon: PersonStanding },
      { key: "dream_log", titleKey: "p_dream_log", descKey: "p_dream_log_desc", icon: Moon },
      { key: "core_transformation", titleKey: "p_core_transformation", descKey: "p_core_transformation_desc", icon: InfinityIcon },
    ],
  },
  {
    pillar: "designate", titleKey: "pillar_designate", descKey: "pillar_designate_desc", color: DESIGNATE,
    practices: [
      { key: "parts_work", titleKey: "p_parts_work", descKey: "p_parts_work_desc", icon: Users },
      { key: "emotion_wheel", titleKey: "p_emotion_wheel", descKey: "p_emotion_wheel_desc", icon: Aperture },
      { key: "abc_model", titleKey: "p_abc_model", descKey: "p_abc_model_desc", icon: GitBranch },
      { key: "cognitive_restructuring", titleKey: "p_cognitive_restructuring", descKey: "p_cognitive_restructuring_desc", icon: Wrench },
    ],
  },
  {
    pillar: "release", titleKey: "pillar_release", descKey: "pillar_release_desc", color: RELEASE,
    practices: [
      { key: "the_work", titleKey: "p_the_work", descKey: "p_the_work_desc", icon: HelpCircle },
      { key: "tre", titleKey: "p_tre", descKey: "p_tre_desc", icon: Activity },
      { key: "emdr", titleKey: "p_emdr", descKey: "p_emdr_desc", icon: ArrowLeftRight },
      { key: "connected_breath", titleKey: "p_connected_breath", descKey: "p_connected_breath_desc", icon: Leaf },
    ],
  },
];

export function getPracticeConfigs(t) {
  return {
    inner_critic: {
      practiceKey: "inner_critic", pillar: "realize", title: t("p_inner_critic_title"), color: REALIZE, icon: Eye,
      steps: [
        { type: "intro", icon: Eye, title: t("p_inner_critic_intro_title"), body: t("p_inner_critic_intro_body") },
        { type: "mood", key: "mood_before", label: t("p_inner_critic_mood_before"), hint: t("p_inner_critic_mood_before_hint") },
        { type: "textarea", key: "critic_voice", label: t("p_inner_critic_voice_label"), placeholder: t("p_inner_critic_voice_ph"), minChars: 5 },
        { type: "choice", key: "critic_tone", label: t("p_inner_critic_tone_label"), options: [
          { value: "harsh", label: t("p_inner_critic_tone_opt_harsh"), emoji: "🔪" },
          { value: "cold", label: t("p_inner_critic_tone_opt_cold"), emoji: "❄️" },
          { value: "pushy", label: t("p_inner_critic_tone_opt_pushy"), emoji: "⚡️" },
          { value: "anxious", label: t("p_inner_critic_tone_opt_anxious"), emoji: "🌪" },
        ] },
        { type: "textarea", key: "critic_protecting", label: t("p_inner_critic_protect_label"), hint: t("p_inner_critic_protect_hint"), minChars: 5 },
        { type: "textarea", key: "critic_response", label: t("p_inner_critic_response_label"), placeholder: t("p_inner_critic_response_ph"), minChars: 3 },
        { type: "mood", key: "mood_after", label: t("p_inner_critic_mood_after") },
      ],
    },
    body_scan: {
      practiceKey: "body_scan", pillar: "realize", title: t("p_body_scan_title"), color: REALIZE, icon: PersonStanding,
      steps: [
        { type: "intro", icon: PersonStanding, title: t("p_body_scan_intro_title"), body: t("p_body_scan_intro_body") },
        { type: "mood", key: "mood_before", label: t("p_body_scan_mood_before") },
        { type: "custom", key: "body_map", label: t("p_body_scan_bodymap_label"), hint: t("p_body_scan_bodymap_hint"),
          render: (v, set) => <BodyMap value={v} onChange={set} />, validate: (v) => !!v?.marks && Object.keys(v.marks).length > 0 },
        { type: "textarea", key: "dominant_sensation", label: t("p_body_scan_sensation_label"), placeholder: t("p_body_scan_sensation_ph"), minChars: 5 },
        { type: "choice", key: "invitation", label: t("p_body_scan_invite_label"), options: [
          { value: "breath", label: t("p_body_scan_invite_breath"), emoji: "🌬" },
          { value: "touch", label: t("p_body_scan_invite_touch"), emoji: "🤲" },
          { value: "words", label: t("p_body_scan_invite_words"), emoji: "💬" },
          { value: "movement", label: t("p_body_scan_invite_movement"), emoji: "🕊" },
          { value: "stillness", label: t("p_body_scan_invite_stillness"), emoji: "🌿" },
        ] },
        { type: "mood", key: "mood_after", label: t("p_body_scan_mood_after") },
      ],
    },
    dream_log: {
      practiceKey: "dream_log", pillar: "realize", title: t("p_dream_log_title"), color: REALIZE, icon: Moon,
      steps: [
        { type: "intro", icon: Moon, title: t("p_dream_log_intro_title"), body: t("p_dream_log_intro_body") },
        { type: "text", key: "dream_title", label: t("p_dream_log_title_label"), placeholder: t("p_dream_log_title_ph") },
        { type: "textarea", key: "dream_narrative", label: t("p_dream_log_narrative_label"), placeholder: t("p_dream_log_narrative_ph"), minChars: 20 },
        { type: "multi", key: "dream_symbols", label: t("p_dream_log_symbols_label"), hint: t("p_dream_log_symbols_hint"), min: 0, options: [
          { value: "water", label: t("p_dream_log_sym_water"), emoji: "🌊" },
          { value: "fire", label: t("p_dream_log_sym_fire"), emoji: "🔥" },
          { value: "animal", label: t("p_dream_log_sym_animal"), emoji: "🦌" },
          { value: "house", label: t("p_dream_log_sym_house"), emoji: "🏠" },
          { value: "person", label: t("p_dream_log_sym_person"), emoji: "👤" },
          { value: "vehicle", label: t("p_dream_log_sym_vehicle"), emoji: "🚗" },
          { value: "threshold", label: t("p_dream_log_sym_threshold"), emoji: "🚪" },
          { value: "light", label: t("p_dream_log_sym_light"), emoji: "💡" },
        ] },
        { type: "choice", key: "dream_tone", label: t("p_dream_log_tone_label"), options: [
          { value: "peaceful", label: t("p_dream_log_tone_peaceful"), emoji: "🕊" },
          { value: "mystery", label: t("p_dream_log_tone_mystery"), emoji: "🌒" },
          { value: "tense", label: t("p_dream_log_tone_tense"), emoji: "⚡️" },
          { value: "grieving", label: t("p_dream_log_tone_grieving"), emoji: "🌧" },
          { value: "joyful", label: t("p_dream_log_tone_joyful"), emoji: "☀️" },
        ] },
        { type: "textarea", key: "dream_question", label: t("p_dream_log_question_label"), placeholder: t("p_dream_log_question_ph"), minChars: 5 },
      ],
    },
    core_transformation: {
      practiceKey: "core_transformation", pillar: "realize", title: t("p_core_transformation_title"), color: REALIZE, icon: InfinityIcon,
      steps: [
        { type: "intro", icon: InfinityIcon, title: t("p_core_intro_title"), body: t("p_core_intro_body") },
        { type: "textarea", key: "trigger", label: t("p_core_trigger_label"), placeholder: t("p_core_trigger_ph"), minChars: 10 },
        { type: "textarea", key: "want", label: t("p_core_want_label"), placeholder: t("p_core_want_ph"), minChars: 5 },
        { type: "textarea", key: "deeper_1", label: t("p_core_deeper1_label"), placeholder: t("p_core_deeper1_ph"), minChars: 5 },
        { type: "textarea", key: "deeper_2", label: t("p_core_deeper2_label"), minChars: 5 },
        { type: "choice", key: "core_state", label: t("p_core_state_label"), options: [
          { value: "peace", label: t("p_core_state_peace"), emoji: "🕊" },
          { value: "love", label: t("p_core_state_love"), emoji: "💗" },
          { value: "being", label: t("p_core_state_being"), emoji: "🌿" },
          { value: "oneness", label: t("p_core_state_oneness"), emoji: "🌌" },
        ] },
        { type: "textarea", key: "integration", label: t("p_core_integration_label"), minChars: 10 },
      ],
    },
    parts_work: {
      practiceKey: "parts_work", pillar: "designate", title: t("p_parts_work_title"), color: DESIGNATE, icon: Users,
      steps: [
        { type: "intro", icon: Users, title: t("p_parts_intro_title"), body: t("p_parts_intro_body") },
        { type: "text", key: "part_name", label: t("p_parts_name_label"), placeholder: t("p_parts_name_ph") },
        { type: "text", key: "part_age", label: t("p_parts_age_label"), placeholder: t("p_parts_age_ph") },
        { type: "choice", key: "part_role", label: t("p_parts_role_label"), options: [
          { value: "protector", label: t("p_parts_role_protector"), emoji: "🛡" },
          { value: "manager", label: t("p_parts_role_manager"), emoji: "🗂" },
          { value: "exile", label: t("p_parts_role_exile"), emoji: "💧" },
          { value: "firefighter", label: t("p_parts_role_firefighter"), emoji: "🔥" },
          { value: "witness", label: t("p_parts_role_witness"), emoji: "👁" },
        ] },
        { type: "textarea", key: "part_message", label: t("p_parts_message_label"), minChars: 10 },
        { type: "textarea", key: "self_response", label: t("p_parts_self_label"), placeholder: t("p_parts_self_ph"), minChars: 5 },
      ],
    },
    emotion_wheel: {
      practiceKey: "emotion_wheel", pillar: "designate", title: t("p_emotion_wheel_title"), color: DESIGNATE, icon: Aperture,
      steps: [
        { type: "intro", icon: Aperture, title: t("p_emotion_wheel_intro_title"), body: t("p_emotion_wheel_intro_body") },
        { type: "mood", key: "mood_before", label: t("p_emotion_wheel_before") },
        { type: "custom", key: "wheel", label: t("p_emotion_wheel_wheel_label"),
          render: (v, set) => <EmotionWheel value={v} onChange={set} />, validate: (v) => v && v.primary },
        { type: "textarea", key: "trigger", label: t("p_emotion_wheel_trigger_label"), placeholder: t("p_emotion_wheel_trigger_ph"), minChars: 5 },
        { type: "mood", key: "mood_after", label: t("p_emotion_wheel_after") },
      ],
    },
    abc_model: {
      practiceKey: "abc_model", pillar: "designate", title: t("p_abc_title"), color: DESIGNATE, icon: GitBranch,
      steps: [
        { type: "intro", icon: GitBranch, title: t("p_abc_intro_title"), body: t("p_abc_intro_body") },
        { type: "textarea", key: "a_activating", label: t("p_abc_a_label"), placeholder: t("p_abc_a_ph"), minChars: 5 },
        { type: "textarea", key: "b_belief", label: t("p_abc_b_label"), placeholder: t("p_abc_b_ph"), minChars: 5 },
        { type: "textarea", key: "c_consequence", label: t("p_abc_c_label"), placeholder: t("p_abc_c_ph"), minChars: 5 },
        { type: "textarea", key: "disputation", label: t("p_abc_dispute_label"), placeholder: t("p_abc_dispute_ph"), minChars: 5 },
        { type: "textarea", key: "new_c", label: t("p_abc_newc_label"), minChars: 5 },
      ],
    },
    cognitive_restructuring: {
      practiceKey: "cognitive_restructuring", pillar: "designate", title: t("p_cr_title"), color: DESIGNATE, icon: Wrench,
      steps: [
        { type: "intro", icon: Wrench, title: t("p_cr_intro_title"), body: t("p_cr_intro_body") },
        { type: "textarea", key: "heavy_thought", label: t("p_cr_heavy_label"), placeholder: t("p_cr_heavy_ph"), minChars: 5 },
        { type: "slider", key: "belief_before", label: t("p_cr_belief_before"), min: 1, max: 10 },
        { type: "multi", key: "distortions", label: t("p_cr_dist_label"), min: 0, options: [
          { value: "all_or_nothing", label: t("p_cr_dist_all_or_nothing"), emoji: "⚫️⚪️" },
          { value: "catastrophizing", label: t("p_cr_dist_catastrophizing"), emoji: "🌪" },
          { value: "mind_reading", label: t("p_cr_dist_mind_reading"), emoji: "🧠" },
          { value: "labeling", label: t("p_cr_dist_labeling"), emoji: "🏷" },
          { value: "should", label: t("p_cr_dist_should"), emoji: "❗️" },
          { value: "personalizing", label: t("p_cr_dist_personalizing"), emoji: "👤" },
        ] },
        { type: "textarea", key: "evidence_for", label: t("p_cr_evidence_for"), minChars: 3 },
        { type: "textarea", key: "evidence_against", label: t("p_cr_evidence_against"), minChars: 3 },
        { type: "textarea", key: "balanced", label: t("p_cr_balanced_label"), placeholder: t("p_cr_balanced_ph"), minChars: 5 },
        { type: "slider", key: "belief_after", label: t("p_cr_belief_after"), min: 1, max: 10 },
      ],
    },
    the_work: {
      practiceKey: "the_work", pillar: "release", title: t("p_the_work_title"), color: RELEASE, icon: HelpCircle,
      steps: [
        { type: "intro", icon: HelpCircle, title: t("p_the_work_intro_title"), body: t("p_the_work_intro_body") },
        { type: "textarea", key: "belief", label: t("p_the_work_belief_label"), placeholder: t("p_the_work_belief_ph"), minChars: 5 },
        { type: "choice", key: "q1", label: t("p_the_work_q1_label"), options: [
          { value: "yes", label: t("p_yesno_yes") }, { value: "no", label: t("p_yesno_no") }, { value: "unsure", label: t("p_yesno_unsure") },
        ] },
        { type: "choice", key: "q2", label: t("p_the_work_q2_label"), options: [
          { value: "yes", label: t("p_yesno_yes") }, { value: "no", label: t("p_yesno_no") },
        ] },
        { type: "textarea", key: "q3", label: t("p_the_work_q3_label"), minChars: 5 },
        { type: "textarea", key: "q4", label: t("p_the_work_q4_label"), minChars: 5 },
        { type: "textarea", key: "turnaround", label: t("p_the_work_turnaround_label"), placeholder: t("p_the_work_turnaround_ph"), minChars: 5 },
      ],
    },
    tre: {
      practiceKey: "tre", pillar: "release", title: t("p_tre_title"), color: RELEASE, icon: Activity,
      steps: [
        { type: "intro", icon: Activity, title: t("p_tre_intro_title"), body: t("p_tre_intro_body") },
        { type: "mood", key: "mood_before", label: t("p_tre_before") },
        { type: "custom", key: "session", label: t("p_tre_session_label"), hint: t("p_tre_session_hint"),
          render: (v, set) => <TREGuide value={v} onChange={set} />, validate: (v) => v && (v.phases_done?.length ?? 0) >= 1 },
        { type: "mood", key: "mood_after", label: t("p_tre_after") },
        { type: "textarea", key: "reflection", label: t("p_tre_reflection_label"), minChars: 5 },
      ],
    },
    emdr: {
      practiceKey: "emdr", pillar: "release", title: t("p_emdr_title"), color: RELEASE, icon: ArrowLeftRight,
      steps: [
        { type: "intro", icon: ArrowLeftRight, title: t("p_emdr_intro_title"), body: t("p_emdr_intro_body") },
        { type: "textarea", key: "target_memory", label: t("p_emdr_target_label"), placeholder: t("p_emdr_target_ph"), minChars: 5 },
        { type: "slider", key: "suds_before", label: t("p_emdr_suds_before"), min: 0, max: 10 },
        { type: "custom", key: "emdr_session", label: t("p_emdr_session_label"), hint: t("p_emdr_session_hint"),
          render: (v, set) => <EMDRAnimator value={v} onChange={set} />, validate: (v) => (v?.completed_rounds ?? 0) >= 10 },
        { type: "slider", key: "suds_after", label: t("p_emdr_suds_after"), min: 0, max: 10 },
        { type: "textarea", key: "shift", label: t("p_emdr_shift_label"), minChars: 5 },
      ],
    },
    connected_breath: {
      practiceKey: "connected_breath", pillar: "release", title: t("p_cb_title"), color: RELEASE, icon: Leaf,
      steps: [
        { type: "intro", icon: Leaf, title: t("p_cb_intro_title"), body: t("p_cb_intro_body") },
        { type: "choice", key: "posture", label: t("p_cb_posture_label"), options: [
          { value: "bed", label: t("p_cb_posture_bed"), emoji: "🛏" },
          { value: "chair", label: t("p_cb_posture_chair"), emoji: "🪑" },
          { value: "floor", label: t("p_cb_posture_floor"), emoji: "🧘" },
        ] },
        { type: "choice", key: "duration", label: t("p_cb_duration_label"), options: [
          { value: "10", label: t("p_cb_dur_starter") },
          { value: "20", label: t("p_cb_dur_20") },
          { value: "30", label: t("p_cb_dur_30") },
          { value: "40", label: t("p_cb_dur_40") },
        ] },
        { type: "mood", key: "mood_before", label: t("p_cb_before") },
        { type: "textarea", key: "intention", label: t("p_cb_intention_label"), placeholder: t("p_cb_intention_ph"), minChars: 5 },
        { type: "multi", key: "experience", label: t("p_cb_experience_label"), min: 0, options: [
          { value: "tears", label: t("p_cb_exp_tears"), emoji: "💧" },
          { value: "tetany", label: t("p_cb_exp_tetany"), emoji: "⚡️" },
          { value: "images", label: t("p_cb_exp_images"), emoji: "🎞" },
          { value: "movement", label: t("p_cb_exp_movement"), emoji: "🕊" },
          { value: "calm", label: t("p_cb_exp_calm"), emoji: "🌊" },
          { value: "heat", label: t("p_cb_exp_heat"), emoji: "🔥" },
          { value: "nothing", label: t("p_cb_exp_nothing"), emoji: "·" },
        ] },
        { type: "mood", key: "mood_after", label: t("p_cb_after") },
        { type: "textarea", key: "reflection", label: t("p_cb_reflection_label"), minChars: 5 },
      ],
    },
  };
}
